import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Typography, Box, CircularProgress, Alert, Paper, Grid, Chip, IconButton
} from '@mui/material';
import { styled } from "@mui/system";
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore'; 
import CertificateList from '../components/CertificateList'; 
import CertificateReviewList from '../components/CertificateReviewList'; 
import { Outlet, useLocation } from 'react-router-dom';

// Icone
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import RefreshIcon from '@mui/icons-material/Refresh';

// --- STYLED COMPONENTS ---

const MainContainer = styled(Box)({
    minHeight: "100vh",
    background: "#F8F9FA",
    padding: "30px",
    fontFamily: "'Inter', sans-serif",
    display: 'flex',
    flexDirection: 'column'
});

const HeaderBox = styled(Box)({
    marginBottom: "30px",
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
});

// Colonna Kanban
const KanbanColumn = styled(Paper)(({ theme, bgcolor }) => ({
    background: "#F1F3F5", // Grigio leggermente più scuro dello sfondo pagina per contrasto
    borderRadius: "16px",
    padding: "16px",
    height: "calc(100vh - 140px)", // Altezza fissa per permettere lo scroll interno
    display: "flex",
    flexDirection: "column",
    borderTop: `4px solid ${bgcolor || '#ccc'}`
}));

const ColumnHeader = styled(Box)({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "16px",
    paddingBottom: "12px",
    borderBottom: "1px solid rgba(0,0,0,0.05)"
});

const ScrollableList = styled(Box)({
    flexGrow: 1,
    overflowY: "auto",
    paddingRight: "8px", // Spazio per la scrollbar
    "&::-webkit-scrollbar": { width: "6px" },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": { background: "#ced6e0", borderRadius: "10px" },
    "&::-webkit-scrollbar-thumb:hover": { background: "#a4b0be" }
});

export default function CertificatesPage() {
    const [certificates, setCertificates] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const location = useLocation();

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const certificatesSnapshot = await getDocs(collection(db, "userCertificates"));
            const certificatesData = certificatesSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

            const usersSnapshot = await getDocs(collection(db, "users"));
            const usersData = usersSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

            setCertificates(certificatesData);
            setUsers(usersData); 
        } catch (err) {
            setError(err.message || "Errore nel recupero dei dati.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const { inReviewCertificates, validCertificates, expiredCertificates, rejectedCertificates } = useMemo(() => {
        const now = new Date();
        const inReview = [], valid = [], expired = [], rejected = [];

        for (const cert of certificates) {
            if (!cert.expiresAt || typeof cert.expiresAt.toDate !== 'function') {
                if (cert.status === 'pending' || cert.isValidated === false) inReview.push(cert);
                continue; 
            }
            const isDateExpired = cert.expiresAt.toDate() < now;

            if (cert.status === 'rejected') rejected.push(cert);
            else if ((cert.status === 'pending' || cert.isValidated === false) && cert.status !== 'rejected') inReview.push(cert);
            else if (cert.status === 'approved' || cert.isValidated === true) {
                if (isDateExpired) expired.push(cert);
                else valid.push(cert);
            }
        }
        return { inReviewCertificates: inReview, validCertificates: valid, expiredCertificates: expired, rejectedCertificates: rejected };
    }, [certificates]);

    const handleApproveCertificate = async (certificateId) => {
        try {
            await updateDoc(doc(db, "userCertificates", certificateId), { isValidated: true, status: "approved" });
            await fetchData();
        } catch (err) {
            console.error("Errore approvazione:", err);
            setError("Errore durante l'approvazione.");
            throw new Error(err); 
        }
    };

    const handleRefuseCertificate = async (certificateId, rejectionNote) => {
        try {
            await updateDoc(doc(db, "userCertificates", certificateId), { isValidated: false, status: "rejected", rejectionNote });
            await fetchData();
        } catch (err) {
            console.error("Errore rifiuto:", err);
            setError("Errore durante il rifiuto.");
            throw new Error(err);
        }
    };

    if (location.pathname !== '/certificates') return <Box sx={{ p: 3 }}><Outlet /></Box>;

    if (loading) return (
        <MainContainer sx={{ justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress sx={{ color: '#a32b2b' }} />
        </MainContainer>
    );

    if (error) return (
        <MainContainer>
            <Alert severity="error">Errore: {error}</Alert>
        </MainContainer>
    );

    return (
        <MainContainer>
            <HeaderBox>
                <Box>
                    <Typography variant="h4" fontWeight="800" sx={{ color: "#2d3436" }}>Gestione Certificati</Typography>
                    <Typography variant="body2" color="text.secondary">Monitora e approva i certificati medici degli iscritti.</Typography>
                </Box>
                <IconButton onClick={fetchData} sx={{ bgcolor: '#fff', boxShadow: 1 }}>
                    <RefreshIcon />
                </IconButton>
            </HeaderBox>
            
            <Grid container spacing={3} sx={{ flexGrow: 1 }}>

                {/* 1. IN REVISIONE (Priorità Alta) */}
                <Grid item xs={12} md={4} lg={3}>
                    <KanbanColumn elevation={0} bgcolor="#ff9f43">
                        <ColumnHeader>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AssignmentIndIcon sx={{ color: '#ff9f43' }} />
                                <Typography variant="subtitle1" fontWeight="bold">Da Revisionare</Typography>
                            </Box>
                            <Chip label={inReviewCertificates.length} size="small" sx={{ bgcolor: '#fff3cd', color: '#856404', fontWeight: 'bold' }} />
                        </ColumnHeader>
                        
                        <ScrollableList>
                            <CertificateReviewList 
                                certificates={inReviewCertificates} 
                                users={users}
                                onApprove={handleApproveCertificate}
                                onRefuse={handleRefuseCertificate}
                            />
                        </ScrollableList>
                    </KanbanColumn>
                </Grid>

                {/* 2. VALIDI */}
                <Grid item xs={12} md={4} lg={3}>
                    <KanbanColumn elevation={0} bgcolor="#2ed573">
                        <ColumnHeader>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircleOutlineIcon sx={{ color: '#2ed573' }} />
                                <Typography variant="subtitle1" fontWeight="bold">Validi</Typography>
                            </Box>
                            <Chip label={validCertificates.length} size="small" sx={{ bgcolor: '#d4edda', color: '#155724', fontWeight: 'bold' }} />
                        </ColumnHeader>
                        <ScrollableList>
                            <CertificateList certificates={validCertificates} users={users} />
                        </ScrollableList>
                    </KanbanColumn>
                </Grid>

                {/* 3. SCADUTI */}
                <Grid item xs={12} md={4} lg={3}>
                    <KanbanColumn elevation={0} bgcolor="#ff4757">
                        <ColumnHeader>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ErrorOutlineIcon sx={{ color: '#ff4757' }} />
                                <Typography variant="subtitle1" fontWeight="bold">Scaduti</Typography>
                            </Box>
                            <Chip label={expiredCertificates.length} size="small" sx={{ bgcolor: '#f8d7da', color: '#721c24', fontWeight: 'bold' }} />
                        </ColumnHeader>
                        <ScrollableList>
                            <CertificateList certificates={expiredCertificates} users={users} />
                        </ScrollableList>
                    </KanbanColumn>
                </Grid>
                
                {/* 4. RIFIUTATI */}
                <Grid item xs={12} md={4} lg={3}>
                    <KanbanColumn elevation={0} bgcolor="#576574">
                        <ColumnHeader>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <HighlightOffIcon sx={{ color: '#576574' }} />
                                <Typography variant="subtitle1" fontWeight="bold">Rifiutati</Typography>
                            </Box>
                            <Chip label={rejectedCertificates.length} size="small" sx={{ bgcolor: '#e2e3e5', color: '#383d41', fontWeight: 'bold' }} />
                        </ColumnHeader>
                        <ScrollableList>
                            <CertificateList certificates={rejectedCertificates} users={users} />
                        </ScrollableList>
                    </KanbanColumn>
                </Grid>

            </Grid>
        </MainContainer>
    );
}